/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement, type ReactNode, useRef, useEffect, useCallback, useState } from 'react';
import { Link } from '@/components/link';
import { Button } from '@/components/ui/button';
import { Menu, Search as SearchIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import MainNav from './main-nav';
import MobileFlyout from './mobile-flyout';
import SearchFlyout from './search-flyout';
import CartBadge from './cart-badge';
import logoHorizontal from '/images/logo-horizontal.svg';

interface HeaderProps {
    beforeHeader?: ReactNode;
    variant?: 'full' | 'checkout';
}

export default function Header({ beforeHeader, variant = 'full' }: HeaderProps): ReactElement {
    const { t } = useTranslation('header');
    const headerRef = useRef<HTMLElement>(null);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [searchOpen, setSearchOpen] = useState(false);

    const openMenu = useCallback(() => setMobileOpen(true), []);
    const openSearch = useCallback(() => setSearchOpen(true), []);

    const updateHeaderHeight = useCallback(() => {
        if (headerRef.current) {
            const height = `${headerRef.current.offsetHeight}px`;
            headerRef.current.style.setProperty('--header-height', height);
            document.documentElement.style.setProperty('--header-height', height);
        }
    }, []);

    useEffect(() => {
        const el = headerRef.current;
        if (!el) return;
        updateHeaderHeight();
        const observer = new ResizeObserver(updateHeaderHeight);
        observer.observe(el);
        return () => observer.disconnect();
    }, [updateHeaderHeight]);

    if (variant === 'checkout') {
        return (
            <header ref={headerRef} className="bg-header-background text-header-foreground sticky top-0 z-50">
                <div className="section-container">
                    <div className="flex items-center h-16">
                        <Link to="/" className="flex-shrink-0 flex items-center" data-testid="header-logo">
                            <img
                                src={logoHorizontal}
                                alt={t('logoAlt')}
                                className="h-5 w-auto [filter:var(--header-logo-filter)]"
                            />
                        </Link>
                        <div className="flex-1" />
                        <CartBadge />
                    </div>
                </div>
            </header>
        );
    }

    return (
        <header ref={headerRef} className="bg-header-background text-header-foreground sticky top-0 z-50">
            <div className="flex justify-end section-container">{beforeHeader}</div>
            <div className="section-container py-3 lg:py-5">
                {/* Desktop: logo left, nav + search icon right */}
                <div className="hidden lg:flex items-center justify-between">
                    <Link to="/" className="flex-shrink-0 flex items-center" data-testid="header-logo">
                        <img
                            src={logoHorizontal}
                            alt={t('logoAlt')}
                            className="h-6 w-auto [filter:var(--header-logo-filter)]"
                        />
                    </Link>
                    <div className="flex items-center gap-x-8">
                        <MainNav orientation="horizontal" />
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={openSearch}
                            aria-label={t('openSearch', 'Open search')}
                            className="hover:bg-transparent hover:opacity-50 transition-opacity"
                            data-testid="header-search-button-desktop">
                            <SearchIcon className="size-[26px]" />
                        </Button>
                    </div>
                </div>

                {/* Mobile: logo left-aligned, search + hamburger on the right (Figma 11957:761).
                    `-ml-3` pulls the logo from the 24px section-container gutter to 12px from the edge,
                    matching the bar's 12px padding (the hamburger glyph already lands 12px from the
                    right via the `-mr-5` offset below). */}
                <div className="flex items-center justify-between lg:hidden">
                    <Link to="/" className="-ml-3 flex shrink-0 items-center" data-testid="header-logo-mobile">
                        <img
                            src={logoHorizontal}
                            alt={t('logoAlt')}
                            className="h-[18px] w-auto [filter:var(--header-logo-filter)]"
                        />
                    </Link>
                    {/* Search + hamburger, right-aligned. The size-9 icon buttons carry 8px of internal
                        padding around each 20px glyph, so gap-0 leaves exactly L (16px) between the two
                        glyphs, and -mr-5 cancels the trailing button's padding so the hamburger glyph
                        lands 12px from the screen edge — matching the Figma spec while keeping 36px taps. */}
                    <div className="-mr-5 flex shrink-0 items-center gap-0">
                        {/* Search icon: opens the search flyout (a bottom sheet), not the nav flyout. */}
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={openSearch}
                            aria-label={t('openSearch', 'Open search')}
                            className="hover:bg-transparent hover:opacity-50 transition-opacity"
                            data-testid="header-search-button">
                            <SearchIcon className="size-5" />
                        </Button>
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={openMenu}
                            aria-label={t('openMenu', 'Open menu')}
                            aria-expanded={mobileOpen}
                            className="hover:bg-transparent hover:opacity-50 transition-opacity"
                            data-testid="header-menu-button">
                            <Menu className="size-5" />
                        </Button>
                    </div>
                </div>
            </div>

            <MobileFlyout open={mobileOpen} onOpenChange={setMobileOpen} />
            <SearchFlyout open={searchOpen} onOpenChange={setSearchOpen} />
        </header>
    );
}
